import React from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { z } from 'zod';
import { toFormikValidationSchema } from 'zod-formik-adapter';
import { Button, Label, TextInput, Textarea } from 'flowbite-react';
import * as wasm from 'sched';
import { v4 as uuidv4 } from 'uuid';

const NativeStakeholderSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  description: z.string().optional(),
});

type NativeStakeholderFormValues = z.infer<typeof NativeStakeholderSchema>;

interface NativeStakeholderFormProps {
  onSubmit: (command: wasm.DatabaseCommand) => Promise<void>;
  onClose: () => void;
  isOpen: boolean;
  /** Called after the create command succeeds, with the new stakeholder's ID. */
  onCreated?: (id: string) => void;
}

const NativeStakeholderForm: React.FC<NativeStakeholderFormProps> = ({ onSubmit, onClose, isOpen, onCreated }) => {
  if (!isOpen) return null;

  const handleSubmit = async (values: NativeStakeholderFormValues) => {
    const id = uuidv4();
    const command: wasm.DatabaseCommand = {
      type: 'CreateItem',
      id,
      item: {
        type: 'Stakeholder',
        value: {
          name: values.name,
          description: values.description || null,
        },
      },
    };
    await onSubmit(command);
    onCreated?.(id);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-cm-bg-surface p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-xl font-semibold mb-4 text-cm-text-primary">Create Stakeholder</h2>

        <Formik
          initialValues={{ name: '', description: '' }}
          validationSchema={toFormikValidationSchema(NativeStakeholderSchema)}
          onSubmit={handleSubmit}
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <div>
                <div className="mb-2">
                  <Label htmlFor="name">Name</Label>
                </div>
                <Field
                  as={TextInput}
                  id="name"
                  name="name"
                  autoFocus
                  placeholder="e.g. Enterprise Customers, Regulatory Bodies"
                />
                <ErrorMessage name="name" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <div className="mb-2">
                  <Label htmlFor="description">Description</Label>
                </div>
                <Field
                  as={Textarea}
                  id="description"
                  name="description"
                  placeholder="Who are they and why do they matter?"
                  rows={3}
                />
              </div>

              <div className="flex justify-end space-x-2 mt-6">
                <Button color="gray" onClick={onClose}>Cancel</Button>
                <Button type="submit" disabled={isSubmitting}>Create Stakeholder</Button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default NativeStakeholderForm;
